import { useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { CheckIcon, CopyIcon, KeyRoundIcon } from 'lucide-react';
import {
  DEMO_ACCESS_GELDIG_DAGEN_MAX,
  DEMO_ACCESS_GELDIG_DAGEN_STANDAARD,
  type CreateDemoAccessCodeResponse,
  type DemoAccessCodeView,
  type DemoAccessStatus,
  type ListDemoAccessCodesResponse,
} from '@kennisbank/shared';
import { Layout } from '../components/Layout.js';
import { useAuth } from '../auth/AuthProvider.js';
import { ApiError, apiGet, apiPost } from '../api/client.js';

/**
 * Beheer > Demo-toegang (besluit 8 okt 2026): persoonlijke toegangscodes voor
 * prospects die de demo bekijken. De code verschijnt één keer, direct na het
 * aanmaken; daarna staat alleen nog de hash in de database.
 *
 * Wie mag dit? Alleen wie op DEMO_ACCESS_ADMIN_EMAILS staat (server-side
 * afgedwongen); /api/me geeft dat door als `demoToegangBeheer`.
 */

const datumFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});
const datumTijdFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});
const langeDatumFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const STATUS_LABEL: Record<DemoAccessStatus, string> = {
  actief: 'Actief',
  verlopen: 'Verlopen',
  ingetrokken: 'Ingetrokken',
};
const STATUS_KLASSE: Record<DemoAccessStatus, string> = {
  actief: 'border-success/30 bg-success/10 text-success',
  verlopen: 'border-ring1-border bg-ring1-bg text-ring1-fg',
  ingetrokken: 'border-ink-100 bg-ink-50 text-ink/70',
};

function mailTekst(r: CreateDemoAccessCodeResponse): string {
  return [
    `Beste ${r.item.naam},`,
    '',
    'Zoals afgesproken krijg je toegang tot de demo van de SUPPOHRT Kennishub. Je ziet daarin hoe Mila, Sara en de andere onderdelen werken, met fictieve gegevens.',
    '',
    `Ga naar: ${r.demoUrl}`,
    `Je persoonlijke toegangscode: ${r.code}`,
    '',
    `De code is geldig tot ${langeDatumFmt.format(new Date(r.item.geldigTot))}. De eerste keer dat je inlogt, vragen we je akkoord te gaan met de gebruiksvoorwaarden. De code is persoonlijk, dus deel hem niet met anderen.`,
    '',
    'Heb je vragen? Reageer gerust op deze mail.',
    '',
    'Met vriendelijke groet,',
    'SUPPOHRT',
  ].join('\n');
}

export function BeheerDemoToegangPage(): JSX.Element {
  const { user } = useAuth();
  const [items, setItems] = useState<DemoAccessCodeView[]>([]);
  const [loading, setLoading] = useState(true);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [naam, setNaam] = useState('');
  const [organisatie, setOrganisatie] = useState('');
  const [dagen, setDagen] = useState(String(DEMO_ACCESS_GELDIG_DAGEN_STANDAARD));
  const [nieuw, setNieuw] = useState<CreateDemoAccessCodeResponse | null>(null);
  const [gekopieerd, setGekopieerd] = useState<'code' | 'mail' | null>(null);
  const [bevestigId, setBevestigId] = useState<string | null>(null);

  const magBeheren = user?.demoToegangBeheer === true;

  async function herlaad(): Promise<void> {
    const resp = await apiGet<ListDemoAccessCodesResponse>('/api/v1/beheer/demo-toegang');
    setItems(resp.items);
  }

  useEffect(() => {
    if (!magBeheren) return;
    (async () => {
      try {
        await herlaad();
      } catch (err) {
        setFout(meldFout(err, 'Laden mislukt'));
      } finally {
        setLoading(false);
      }
    })();
  }, [magBeheren]);

  if (!magBeheren) return <Navigate to="/" replace />;

  const dagenGetal = Number(dagen);
  const dagenGeldig =
    Number.isInteger(dagenGetal) && dagenGetal >= 1 && dagenGetal <= DEMO_ACCESS_GELDIG_DAGEN_MAX;
  const kanAanmaken =
    naam.trim().length > 0 && organisatie.trim().length > 0 && dagenGeldig && !bezig;

  async function maakAan(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!kanAanmaken) return;
    setBezig(true);
    setFout(null);
    setGekopieerd(null);
    try {
      const resp = await apiPost<CreateDemoAccessCodeResponse>('/api/v1/beheer/demo-toegang', {
        naam: naam.trim(),
        organisatie: organisatie.trim(),
        geldigDagen: dagenGetal,
      });
      setNieuw(resp);
      setNaam('');
      setOrganisatie('');
      setDagen(String(DEMO_ACCESS_GELDIG_DAGEN_STANDAARD));
      await herlaad();
    } catch (err) {
      setFout(meldFout(err, 'Aanmaken mislukt'));
    } finally {
      setBezig(false);
    }
  }

  async function trekIn(id: string): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      await apiPost(`/api/v1/beheer/demo-toegang/${encodeURIComponent(id)}/intrekken`);
      setBevestigId(null);
      await herlaad();
    } catch (err) {
      setFout(meldFout(err, 'Intrekken mislukt'));
    } finally {
      setBezig(false);
    }
  }

  async function kopieer(wat: 'code' | 'mail', tekst: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(tekst);
      setGekopieerd(wat);
    } catch {
      setFout('Kopiëren lukte niet. Selecteer de tekst en kopieer hem zelf.');
    }
  }

  return (
    <Layout>
      <div className="mx-auto w-full max-w-5xl px-4 py-6">
        <header className="mb-4">
          <h1 className="text-xl font-bold text-ink-900">Demo-toegang</h1>
          <p className="mt-1 text-sm text-ink/70">
            Maak per prospect een persoonlijke toegangscode voor de demo van de Kennishub. Je ziet
            de code één keer, direct na het aanmaken. Trek je een code in of verloopt hij, dan is
            die prospect binnen een minuut uitgelogd.
          </p>
        </header>

        {fout && (
          <div
            role="alert"
            className="mb-4 rounded-2xl border border-error/30 bg-error/5 px-4 py-3 text-sm text-error"
          >
            {fout}
          </div>
        )}

        {nieuw && (
          <section
            aria-live="polite"
            className="mb-6 rounded-2xl border border-brand-200 bg-brand-50 p-5 shadow-card"
          >
            <h2 className="text-base font-bold text-ink-900">
              Code voor {nieuw.item.naam} ({nieuw.item.organisatie})
            </h2>
            <p className="mt-1 text-sm text-ink/80">
              Bewaar of verstuur hem nu: na het sluiten van dit blok kun je de code niet meer
              opvragen. Kwijt? Trek hem in en maak een nieuwe.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className="select-all rounded-xl border border-brand-200 bg-white px-4 py-3 font-mono text-xl font-semibold tracking-widest text-ink-900 sm:text-2xl">
                {nieuw.code}
              </span>
              <button
                type="button"
                onClick={() => void kopieer('code', nieuw.code)}
                className="inline-flex items-center gap-2 rounded-full border border-ink-100 bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-brand-700"
              >
                {gekopieerd === 'code' ? (
                  <CheckIcon className="h-4 w-4 text-success" aria-hidden />
                ) : (
                  <CopyIcon className="h-4 w-4" aria-hidden />
                )}
                {gekopieerd === 'code' ? 'Gekopieerd' : 'Kopieer code'}
              </button>
            </div>
            <label className="mt-4 block">
              <span className="mb-1 block text-sm font-semibold text-ink-900">
                Tekst om te mailen
              </span>
              <textarea
                readOnly
                rows={12}
                value={mailTekst(nieuw)}
                className="w-full rounded-xl border border-ink-100 bg-white px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
              />
            </label>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void kopieer('mail', mailTekst(nieuw))}
                className="inline-flex items-center gap-2 rounded-full bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800"
              >
                {gekopieerd === 'mail' ? (
                  <CheckIcon className="h-4 w-4" aria-hidden />
                ) : (
                  <CopyIcon className="h-4 w-4" aria-hidden />
                )}
                {gekopieerd === 'mail' ? 'Tekst gekopieerd' : 'Kopieer mailtekst'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNieuw(null);
                  setGekopieerd(null);
                }}
                className="rounded-full border border-ink-100 bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-brand-700"
              >
                Klaar, sluiten
              </button>
            </div>
          </section>
        )}

        <form
          onSubmit={(e) => void maakAan(e)}
          className="mb-6 rounded-2xl border border-ink-100 bg-white p-5 shadow-card"
        >
          <h2 className="mb-3 flex items-center gap-2 text-base font-bold text-ink-900">
            <KeyRoundIcon className="h-4 w-4 text-brand-700" aria-hidden />
            Nieuwe toegangscode
          </h2>
          <div className="grid gap-3 sm:grid-cols-[1fr_1fr_10rem]">
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-ink-900">Naam prospect</span>
              <input
                type="text"
                value={naam}
                maxLength={120}
                onChange={(e) => setNaam(e.target.value)}
                className="w-full rounded-xl border border-ink-100 px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-ink-900">Organisatie</span>
              <input
                type="text"
                value={organisatie}
                maxLength={160}
                onChange={(e) => setOrganisatie(e.target.value)}
                className="w-full rounded-xl border border-ink-100 px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-semibold text-ink-900">Geldig (dagen)</span>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={DEMO_ACCESS_GELDIG_DAGEN_MAX}
                value={dagen}
                onChange={(e) => setDagen(e.target.value)}
                aria-invalid={!dagenGeldig}
                className="w-full rounded-xl border border-ink-100 px-3 py-2 text-sm text-ink focus:border-brand-700 focus:outline-none"
              />
            </label>
          </div>
          {!dagenGeldig && (
            <p className="mt-2 text-xs text-error">
              Kies een geldigheid van 1 tot en met {DEMO_ACCESS_GELDIG_DAGEN_MAX} dagen.
            </p>
          )}
          <button
            type="submit"
            disabled={!kanAanmaken}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-brand-700 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-40"
          >
            {bezig ? 'Bezig…' : 'Code aanmaken'}
          </button>
        </form>

        <h2 className="mb-2 text-base font-bold text-ink-900">Uitgegeven codes</h2>
        {loading ? (
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-10 text-center text-sm text-ink/55">
            Laden…
          </div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-ink-100 bg-white px-6 py-10 text-center text-sm text-ink/55">
            Nog geen codes uitgegeven.
          </div>
        ) : (
          <ul className="flex flex-col gap-3">
            {items.map((item) => (
              <li
                key={item.id}
                className="rounded-2xl border border-ink-100 bg-white px-5 py-4 shadow-card"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-ink-900">{item.naam}</h3>
                    <p className="text-sm text-ink/70">{item.organisatie}</p>
                  </div>
                  <span
                    className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${STATUS_KLASSE[item.status]}`}
                  >
                    {STATUS_LABEL[item.status]}
                  </span>
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs tabular-nums sm:grid-cols-4">
                  <Gegeven label="Aangemaakt" waarde={datumFmt.format(new Date(item.createdAt))} />
                  <Gegeven label="Geldig tot" waarde={datumFmt.format(new Date(item.geldigTot))} />
                  <Gegeven
                    label="Laatst gebruikt"
                    waarde={
                      item.laatstGebruiktOp
                        ? datumTijdFmt.format(new Date(item.laatstGebruiktOp))
                        : 'Nog niet'
                    }
                  />
                  <Gegeven
                    label="Voorwaarden"
                    waarde={
                      item.voorwaardenAkkoordOp
                        ? `Akkoord ${datumTijdFmt.format(new Date(item.voorwaardenAkkoordOp))}`
                        : 'Nog niet'
                    }
                  />
                </dl>
                {item.status === 'actief' && (
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {bevestigId === item.id ? (
                      <>
                        <span className="text-sm text-ink">Toegang van {item.naam} intrekken?</span>
                        <button
                          type="button"
                          disabled={bezig}
                          onClick={() => void trekIn(item.id)}
                          className="rounded-full bg-error px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90 disabled:opacity-40"
                        >
                          Ja, intrekken
                        </button>
                        <button
                          type="button"
                          disabled={bezig}
                          onClick={() => setBevestigId(null)}
                          className="rounded-full border border-ink-100 px-3 py-1.5 text-xs font-semibold text-ink hover:border-brand-700 disabled:opacity-40"
                        >
                          Annuleren
                        </button>
                      </>
                    ) : (
                      <button
                        type="button"
                        disabled={bezig}
                        onClick={() => setBevestigId(item.id)}
                        className="rounded-full border border-ink-100 px-3 py-1.5 text-xs font-semibold text-error hover:border-error disabled:opacity-40"
                      >
                        Intrekken
                      </button>
                    )}
                  </div>
                )}
                {item.status === 'ingetrokken' && item.ingetrokkenOp && (
                  <p className="mt-3 text-xs text-ink/60">
                    Ingetrokken op {datumTijdFmt.format(new Date(item.ingetrokkenOp))}
                    {item.ingetrokkenDoorEmail ? ` door ${item.ingetrokkenDoorEmail}` : ''}.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}

function Gegeven({ label, waarde }: { label: string; waarde: string }): JSX.Element {
  return (
    <div>
      <dt className="text-ink/55">{label}</dt>
      <dd className="font-semibold text-ink">{waarde}</dd>
    </div>
  );
}

function meldFout(err: unknown, prefix: string): string {
  return err instanceof ApiError
    ? `${prefix} (${err.status}): ${err.message}`
    : `${prefix}. Probeer het later opnieuw.`;
}
